import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Order } from '../classes/order';
import { Supplier } from '../classes/supplier';

type SupplierStartupResponse = {
    supplier: unknown;
    orders?: unknown[];
    emailChangeDeprecated?: boolean;
};

type UploadLinkResponse = {
    uploadUrl: string;
    documentId: string;
};

@Injectable({
    providedIn: 'root'
})
export class SupplierApiService {
    private readonly supplierBaseUrl = environment.apiUrls.supplier;
    private readonly orderBaseUrl = environment.apiUrls.order;

    constructor(private readonly http: HttpClient) {}

    public async fetchStartupData(): Promise<{ orders: Order[]; supplier: Supplier; emailChangeDeprecated: boolean }> {
        const response = await firstValueFrom(this.http.get<SupplierStartupResponse>(`${this.supplierBaseUrl}/me`));
        const supplier = Supplier.jsonToSupplier(response.supplier);

        return {
            supplier,
            orders: Order.jsonToOrders(response.orders ?? []),
            emailChangeDeprecated: response.emailChangeDeprecated ?? true
        };
    }

    public async updateOrders(order: Order): Promise<void> {
        await firstValueFrom(
            this.http.post<void>(`${this.orderBaseUrl}/user`, {
                order: Order.orderToJson(order),
                newOrder: Boolean(order.newOrder),
                ...(order.newOrder ? {} : { index: order.index })
            })
        );
    }

    public async getNewID(site: string): Promise<string> {
        const response = await firstValueFrom(this.http.patch<{ id: string }>(`${this.orderBaseUrl}/${site}`, null));
        return response.id;
    }

    public async uploadNewFile(file: File): Promise<string> {
        const { uploadUrl, documentId } = await firstValueFrom(
            this.http.post<UploadLinkResponse>(`${this.orderBaseUrl}/upload-link`, {
                fileName: file.name,
                contentType: file.type || undefined
            })
        );

        const response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: file.type
                ? {
                      'Content-Type': file.type
                  }
                : undefined,
            body: file
        });

        if (!response.ok) {
            throw new Error(`File upload failed with status ${response.status}.`);
        }

        return documentId;
    }
}
